// The fee reserve capsule (SCENES B10, restyled by mock-website/round-6/plan/APP-R3.md §5): the Fee reserve card's
// capsule from the landing page (a pale body, a darker right-side band, a dark window strip), built in CSS and DOM.
// Look only: it takes its values as props from the reading that feeds the words beside it and never reads the
// chain itself. The capsule is aria-hidden; the words carry the state. Nothing inside it is cobalt: the fee reserve
// is an ordinary key, outside the vault's protection.
//
// FeeChip    Overview, beside "SOL in your vault": a 24 x 64 capsule whose window fills pale to min(N, 10) / 10
//            (data-fill, no style attribute: the production CSP), the eyebrow "Fee reserve" with its status dot,
//            and one line. No SOL figure. One pulse only when a later reading in the same view holds more lamports.
// FeeCapsule GasTankPanel: a 36 x 96 status object (glass, window, core), no level.
import { type Ref, useEffect, useRef } from 'react';
import type { ReserveReading } from '../engine';

export type CapsuleState = 'checking' | 'error' | 'last' | 'low' | 'funded' | 'idle';

export function FeeCapsule({ size, state, fill = 0, coreRef }: { size: 'chip' | 'card'; state: CapsuleState; fill?: number; coreRef?: Ref<HTMLSpanElement> }) {
  return (
    <span className={`fee-capsule ${size}`} data-state={state} data-fill={String(fill)} aria-hidden="true">
      <span className="fee-capsule-window">
        <span className="fee-capsule-level" />
        <span className="fee-capsule-core" ref={coreRef} />
        <span className="fee-capsule-bubble" />
      </span>
    </span>
  );
}

/** The Overview chip, one-to-one with the status strip's tones (B10 "Chip"). */
export function FeeChip({ tank, error }: { tank: ReserveReading | null; error: string | null }) {
  const low = !!tank && tank.lamports < tank.plainAction;
  const state: CapsuleState = tank ? (error ? 'last' : low ? 'low' : 'funded') : error ? 'error' : 'checking';
  const n = tank ? tank.actions : 0;
  const fill = tank && !low ? Math.min(n, 10) : 0;
  const line = tank
    ? error
      ? `About ${n} ${n === 1 ? 'action' : 'actions'}, last read`
      : low
        ? 'Too low'
        : `About ${n} ${n === 1 ? 'action' : 'actions'}`
    : error
      ? 'Could not check'
      : 'Checking…';

  // The one pulse: a later successful reading in this mounted view holds more lamports than the one before.
  const core = useRef<HTMLSpanElement>(null);
  const prev = useRef<bigint | null>(null);
  const lamports = tank && !error ? tank.lamports : null;
  useEffect(() => {
    if (lamports === null) return;
    const before = prev.current;
    prev.current = lamports;
    if (before === null || lamports <= before || !core.current) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    core.current.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.45)' }, { transform: 'scale(1)' }], { duration: 900, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    // the bubble rises from the core to about 70 % of the window's height and fades (B10: 200–1100 ms)
    const bubble = core.current.parentElement?.querySelector('.fee-capsule-bubble');
    const rise = Math.round(0.7 * (core.current.parentElement?.clientHeight ?? 48));
    bubble?.animate(
      [
        { transform: 'translateY(0)', opacity: 1 },
        { transform: `translateY(-${rise}px)`, opacity: 0 },
      ],
      { duration: 900, delay: 200, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' },
    );
  }, [lamports]);

  return (
    <div className="fee-chip" data-state={state}>
      <FeeCapsule size="chip" state={state} fill={fill} coreRef={core} />
      <div className="fee-chip-text">
        <span className="eyebrow">Fee reserve</span>
        <span className="fee-chip-line">{line}</span>
      </div>
    </div>
  );
}
